import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Divider, Chip, Paper,
} from '@mui/material';
import { publicPassAPI } from '../services/api';
import HealthPassCard from '../components/TravelerScreening/HealthPassCard';
import type { PassRecord } from '../components/TravelerScreening/HealthPassCard';

const isPassDebugEnabled = () => {
  try {
    const qp = new URLSearchParams(window.location.search);
    const byQuery = qp.get('passDebug') === '1';
    const byLs = window.localStorage.getItem('passDebug') === '1';
    return byQuery || byLs;
  } catch {
    return false;
  }
};

const passDebug = (...args: any[]) => {
  if (!isPassDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[PassDebug]', ...args);
};

const fmt = (val: any) => {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

const fmtDate = (val: any) => {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(val); }
};

const SectionTitle: React.FC<{ label: string }> = ({ label }) => (
  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1, color: '#555', mt: 2.5, mb: 1 }}>
    {label}
  </Typography>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
    <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#111' }}>{value}</Typography>
  </Box>
);

const PublicPassPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    passDebug('Mounted PublicPassPage');
    passDebug('Location', {
      href: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      origin: window.location.origin,
    });
    passDebug('Router params', { token });

    if (!token) { setError('No token in URL.'); setLoading(false); return; }
    passDebug('Calling public pass API', { token });

    publicPassAPI.getPass(token)
      .then((d: any) => {
        passDebug('API success', d);
        setData(d);
        setLoading(false);
      })
      .catch((e: any) => {
        passDebug('API error', {
          message: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
        setError('Pass not found or link is invalid.');
        setLoading(false);
      });
  }, [token]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <CircularProgress />
      <Typography sx={{ mt: 2, color: '#555' }}>Loading health pass...</Typography>
    </Box>
  );

  if (error || !data) return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Alert severity="error">{error || 'Pass not found.'}</Alert>
      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#999' }}>Token: {token}</Typography>
    </Box>
  );

  const { pass_record, screening } = data;

  const passRecord: PassRecord | null = pass_record ? {
    pass_type:         pass_record.pass_type,
    full_name:         pass_record.full_name,
    id_number:         pass_record.id_number,
    country_of_origin: screening?.country_of_origin,
    screening_type:    pass_record.screening_type,
    screening_status:  pass_record.screening_status,
    action_required:   pass_record.action_required,
    next_step:         pass_record.next_step,
    issued_at:         pass_record.issued_at,
    valid_until:       pass_record.valid_until,
    id:                pass_record.id,
    screening_id:      data.screening_id,
    pass_token:        token,
  } : null;

  const symptoms = [
    screening?.symptom_fever     && 'Fever',
    screening?.symptom_cough     && 'Cough',
    screening?.symptom_breathing && 'Difficulty Breathing',
    screening?.symptom_vomiting  && 'Vomiting',
    screening?.symptom_diarrhea  && 'Diarrhea',
    screening?.symptom_jaundice  && 'Jaundice',
    screening?.symptom_rash      && 'Rash',
    screening?.symptom_bleeding  && 'Bleeding',
  ].filter(Boolean) as string[];

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', px: 2, py: 4 }}>
      {/* Branding */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" color="#1f93ff">Voyage Santé+</Typography>
        <Typography variant="body2" color="text.secondary">CAR-Ebola AI Connect · Ministry of Health</Typography>
      </Box>

      {/* Pass Card */}
      {passRecord ? (
        <HealthPassCard passRecord={passRecord} />
      ) : (
        <Alert severity="warning">No health pass generated for this screening.</Alert>
      )}

      {/* Full Q&A */}
      {screening && (
        <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>Screening Details</Typography>
          <Divider sx={{ mb: 1 }} />

          <SectionTitle label="Entry Information" />
          <Row label="Entry Type"              value={fmt(screening.entry_type)} />
          <Row label="Entry City"              value={fmt(screening.entry_city)} />
          <Row label="Entry Country"           value={fmt(screening.entry_country)} />
          <Row label="Arrival Date"            value={fmtDate(screening.arrival_date)} />
          <Row label="Arrival Time"            value={fmt(screening.arrival_time)} />
          <Row label="Transport Number"        value={fmt(screening.transport_number)} />
          <Row label="Country of Origin"       value={fmt(screening.country_of_origin)} />
          <Row label="Countries Visited (21d)" value={(screening.countries_visited_21d || []).join(', ') || '—'} />

          <SectionTitle label="Personal Details" />
          <Row label="Full Name"     value={fmt(screening.full_name)} />
          <Row label="Sex"           value={fmt(screening.sex)} />
          <Row label="Date of Birth" value={fmtDate(screening.date_of_birth)} />
          <Row label="Age"           value={fmt(screening.age)} />
          <Row label="Nationality"   value={fmt(screening.nationality)} />
          <Row label="ID / Passport" value={fmt(screening.id_number)} />

          <SectionTitle label="Health Assessment" />
          <Row label="Temperature (°C)" value={fmt(screening.temperature)} />
          <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
            <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Symptoms</Typography>
            <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {symptoms.length > 0
                ? symptoms.map(s => <Chip key={s} label={s} size="small" color="error" variant="outlined" sx={{ fontSize: '0.72rem' }} />)
                : <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>None</Typography>
              }
            </Box>
          </Box>
          <Row label="Contact with Sick Person" value={screening.contact_with_sick ? 'Yes' : 'No'} />
          <Row label="Visited Epidemic Area"    value={screening.visited_epidemic_area ? 'Yes' : 'No'} />
          <Row label="Epidemic Details"         value={fmt(screening.epidemic_details)} />

          <SectionTitle label="Vaccination" />
          <Row label="Yellow Fever Vaccinated" value={screening.yellow_fever_vaccinated ? 'Yes' : 'No'} />
          <Row label="Yellow Fever Date"       value={fmtDate(screening.yellow_fever_date)} />

          <SectionTitle label="Risk Assessment" />
          <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
            <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Risk Level</Typography>
            <Chip
              label={screening.risk_level || '—'}
              size="small"
              color={screening.risk_level === 'high' ? 'error' : screening.risk_level === 'moderate' ? 'warning' : 'success'}
              sx={{ fontWeight: 700, textTransform: 'uppercase' }}
            />
          </Box>
          <Row label="Screened At" value={fmtDate(screening.created_at)} />
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
        This pass is read-only. Issued by CAR-Ebola AI Connect · Not transferable.
      </Typography>
    </Box>
  );
};

export default PublicPassPage;
